import { getQuizScoresPaginated } from '@services/youtube-platform.service';

import type {
  LeaderboardCursor,
  LeaderboardFilter,
  LeaderboardPage,
  LeaderboardResponse,
} from './types';

export const PER_PAGE = 20;

/** `getWithAuth` sets no axios timeout (only `postWithAuth` does), so a hung GET never settles. */
const REQUEST_DEADLINE_MS = 30_000;

/**
 * `@services/youtube-platform.service` is untyped JS. Assert the one function we
 * need at this boundary rather than converting the whole module.
 */
const fetchPage = getQuizScoresPaginated as (
  streamId: string,
  params: {
    filter: LeaderboardFilter;
    perPage: number;
    questionNumber?: number;
    afterScore?: number;
    afterName?: string;
  },
) => Promise<LeaderboardResponse>;

export class LeaderboardError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LeaderboardError';
  }
}

const withDeadline = <T,>(promise: Promise<T>, ms: number): Promise<T> => new Promise<T>((resolve, reject) => {
  const timer = setTimeout(
    () => reject(new LeaderboardError('Leaderboard request timed out')),
    ms,
  );
  promise.then(resolve, reject).finally(() => clearTimeout(timer));
});

interface FetchScoresArgs {
  streamId: string;
  filter: LeaderboardFilter;
  questionNumber?: number;
  cursor: LeaderboardCursor | null;
}

/**
 * Fetches one page of scores. The API resolves with `{ status: false }` rather than
 * rejecting on failure, so translate that into a throw for react-query.
 */
export const fetchScoresPage = async ({
  streamId,
  filter,
  questionNumber,
  cursor,
}: FetchScoresArgs): Promise<LeaderboardPage> => {
  const response = await withDeadline(
    fetchPage(streamId, {
      filter,
      perPage: PER_PAGE,
      ...(questionNumber !== undefined ? { questionNumber } : {}),
      ...(cursor ? { afterScore: cursor.afterScore, afterName: cursor.afterName } : {}),
    }),
    REQUEST_DEADLINE_MS,
  );

  if (!response?.status) {
    throw new LeaderboardError('Failed to fetch the quiz leaderboard scores');
  }

  return response.entity?.rows ?? [];
};
